import {
  Anchor,
  Button,
  Container,
  Divider,
  Grid,
  SegmentedControl,
  Space,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
// import { showNotification } from '@mantine/notifications';
import { IconCheck, IconCircleCheck, IconCircleX } from '@tabler/icons-react';
import cx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getAvailablePrices, KEY_MYGPT_TOKEN_EXPIRESIN } from '@mygpt/app';
import { showNotification } from '@mantine/notifications';
import Cookies from 'js-cookie';
import { useGPTStore } from '@/store';
import { PlanData, PlanInfo } from '@/utils/interface';

import { AuthChoiceModal } from '@/components/AuthChoiceModal';
import { OrderConfirmModal } from '@/components/OrderConfirmModal';
import { usePromoFromUrl } from '@/components/Pricing/usePromoFromUrl';

import classes from './index.module.css';

const Pricing = () => {
  const theme = useMantineTheme();
  const isSm = useMediaQuery('(max-width: 640px)');
  const { t } = useTranslation();
  const { userInfo } = useGPTStore();
  const [paymentCycle, setPaymentCycle] = useState('monthly');
  const [planType, setPlanType] = useState('0');
  const [planList, setPlanList] = useState<Array<PlanInfo>>([]);
  const [checkoutLoading, setCheckoutLoading] = useState<number | null>(null);

  const getPricing = async () => {
    console.log('[Pricing] getPricing started');
    try {
      // 从/billing/prices获取价格信息
      console.log('[Pricing] Calling getAvailablePrices...');
      const res = await getAvailablePrices();
      console.log('[Pricing] API response:', res);
      const prices = res.data || [];
      console.log('[Pricing] Prices count:', prices.length);

      // 将价格按plan分组并转换为PlanInfo格式
      const planMap = new Map<number, { monthly?: string; yearly?: string }>();

      // 定义plan_name到order的映射（根据实际API返回调整）
      const planOrderMap: Record<string, number> = {
        Free: 0,
        free: 0,
        Basic: 1,
        basic: 1,
        Starter: 1,
        starter: 1,
        Pro: 2,
        pro: 2,
        Professional: 2,
        professional: 2,
        Premium: 3,
        premium: 3,
        Business: 3,
        business: 3,
        Enterprise: 4,
        enterprise: 4,
      };

      prices.forEach((price) => {
        // 使用plan_name确定套餐order
        if (!price.plan_name) {
          console.warn('Price missing plan_name:', price);
          return;
        }

        const order = planOrderMap[price.plan_name];
        if (order === undefined) {
          console.warn(`Unknown plan name: ${price.plan_name}`);
          return;
        }

        if (!planMap.has(order)) {
          planMap.set(order, {});
        }

        const planData = planMap.get(order)!;
        if (price.billing_interval === 'month') {
          planData.monthly = price.id;
        } else if (price.billing_interval === 'year') {
          planData.yearly = price.id;
        }
      });

      // 转换为PlanInfo数组，只包含有价格信息的付费套餐
      const planList: PlanInfo[] = Array.from(planMap.entries())
        .map(([order, prices]) => ({
          order,
          api_id_monthly: prices.monthly,
          api_id_yearly: prices.yearly,
        }))
        .sort((a, b) => a.order - b.order);

      console.log('[Pricing] Final planList:', planList);
      setPlanList(planList);
    } catch (error) {
      console.error('[Pricing] Failed to fetch pricing:', error);
      console.error('[Pricing] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });
      showNotification({
        color: 'red',
        message: t('pricing.fetch_error', '获取价格信息失败'),
      });
    }
  };

  // v0.5: 二维码扫描深链 ?promo=XXX，传入 Modal 作为初始值
  const urlPromo = usePromoFromUrl();
  const [confirmPriceId, setConfirmPriceId] = useState<string | null>(null);
  // v0.9.3: 未登录用户点订阅时弹出登录/注册选择
  const [authChoiceOpen, setAuthChoiceOpen] = useState(false);

  const goCheckout = async (data: PlanInfo) => {
    const nowTime = new Date().getTime();
    const expiresTime = Number(Cookies.get(KEY_MYGPT_TOKEN_EXPIRESIN()));
    const origin = window.location.origin;
    const price_id = data[`api_id_${paymentCycle}` as keyof PlanInfo] as string;

    // 检查是否登录
    if (!expiresTime || expiresTime <= nowTime || !userInfo) {
      // v0.9.3: 弹出登录/注册选择 Modal，保留 ?promo= 等 URL param
      setAuthChoiceOpen(true);
      return;
    }

    // 检查是否有price_id
    if (!price_id) {
      showNotification({
        color: 'red',
        message: t('pricing.price_not_available', '该套餐暂不可用，请联系销售'),
      });
      return;
    }

    // v0.5: 不再直接调 Stripe，先打开订单确认 Modal
    setCheckoutLoading(data.order);
    setConfirmPriceId(price_id);
  };

  const getPrice = (price: string) => {
    return paymentCycle === 'monthly' ? price : +price * 10;
  };

  useEffect(() => {
    console.log('[Pricing] Component mounted, calling getPricing');
    getPricing();
  }, []);

  // fix mantine grid scrollbar bug
  useEffect(() => {
    document.body.style.overflowX = 'hidden';

    return () => {
      document.body.style.overflowX = 'inherit';
    };
  }, [planList]);

  const Icon = ({ value }: { value: string }) => {
    switch (value) {
      case '0':
        return <IconCircleX style={{ color: theme.colors.dark[4] }} />;
      case '1':
        return <IconCircleCheck style={{ color: theme.colors.indigo[8] }} />;
      case '2':
        return <Text>{t('pricing.contact')}</Text>;
      default:
        return <Text>{value}</Text>;
    }
  };

  const PlanTitle = () => {
    return (
      <div className={cx(classes.tableCol, classes.titleCol, 'text-left')}>
        <div className={classes.noBorder}></div>
        <div className={classes.noBorder}></div>
        {/* <div className={classes.noBorder}></div> */}
        <div className={classes.tableSubTitle}>{t('pricing.title.equity')}</div>
        <div>{t('pricing.title.chat_bot')}</div>
        <div>{t('pricing.title.trainings')}</div>
        <div>{t('pricing.title.messages')}</div>
        <div>{t('pricing.title.file_size')}</div>
        <div>{t('pricing.title.storage')}</div>
        {/* <div>{t('pricing.title.source')}</div> */}
        <div className={classes.tableSubTitle}>{t('pricing.title.advanced')}</div>
        <div>{t('pricing.title.chatgpt4')}</div>
        {/* <div>{t('pricing.title.data_analysis')}</div> */}
        <div>{t('pricing.title.record_export')}</div>
        {/* <div>{t('pricing.title.insights')}</div> */}
        <div>{t('pricing.title.faqs')}</div>
        <div>{t('pricing.title.unbrand')}</div>
        <div>{t('pricing.title.local')}</div>
        <div>{t('pricing.title.api')}</div>
        {/* <div className={classes.tableSubTitle}>{t('pricing.title.sync')}</div>
        <div>{t('pricing.title.messenger')}</div>
        <div>{t('pricing.title.facebook')}</div>
        <div>{t('pricing.title.whatsapp')}</div>
        <div>{t('pricing.title.telegram')}</div>
        <div>{t('pricing.title.line')}</div> */}
        <div className={classes.tableSubTitle}>{t('pricing.title.gift_functon')}</div>
        <div>{t('pricing.title.brand')}</div>
        <div>{t('pricing.title.custom')}</div>
        <div>{t('pricing.title.customer')}</div>
        <div>{t('pricing.title.feature')}</div>
        <div>{t('pricing.title.team')}</div>
      </div>
    );
  };

  const Plan = ({ data }: { data: PlanInfo }) => {
    const detail = t(`pricing.plan_${data.order}`, { returnObjects: true }) as PlanData;
    const noPay = [0, 4].includes(data.order);
    const hasPriceId = Boolean(data.api_id_monthly || data.api_id_yearly);

    return (
      <div className={classes.tableCol}>
        <div className={cx(classes.noBorder, 'text-xl')}>{detail.title}</div>
        <div className={classes.noBorder}>
          {!noPay && hasPriceId && (
            <Button
              radius="xl"
              size="xs"
              loading={checkoutLoading === data.order}
              onClick={() => {
                goCheckout(data);
              }}
            >
              {t('pricing.purchase')}
            </Button>
          )}
        </div>
        <div className={cx(classes.noBorder, classes.price)}>
          {/* {noPay ? (
            data.order === 4 ? (
              <Anchor size="xs" href="/support.html" target="_blank">
                {detail.price}
              </Anchor>
            ) : (
              <Text fz={12}>{detail.price}</Text>
            )
          ) : (
            <>
              <Text fz={30}>${getPrice(detail.price)}</Text>
              <Text fz={10} c="#999">
                /{t(paymentCycle === 'monthly' ? 'pricing.month' : 'pricing.year')}
              </Text>
            </>
          )} */}
          {data.order === 0 ? (
            <Anchor size="sm" href={import.meta.env.VITE_ADMIN_URL} target="_blank">
              {t('register')}
            </Anchor>
          ) : (
            <Anchor size="sm" href="https://www.sparticle.com/ja/contact-us-jp" target="_blank">
              {t('pricing.contact_us')}
            </Anchor>
          )}
        </div>
        <div className={classes.tableSubTitle}></div>
        <div>{detail.chat_bot ? detail.chat_bot : t('pricing.infinity')}</div>
        <div>{detail.trainings + '/' + t('pricing.month')}</div>
        <div>{detail.messages + '/' + t('pricing.month')}</div>
        <div>{detail.file_size}</div>
        <div>{detail.storage}</div>
        {/* <div>{detail.source}</div> */}
        <div className={classes.tableSubTitle}></div>
        <div>
          <Icon value={detail.chatgpt4} />
        </div>
        {/* <div>
          <Icon value={detail.data_analysis} />
        </div> */}
        <div>
          <Icon value={detail.record_export} />
        </div>
        {/* <div>
          <Icon value={detail.insights} />
        </div> */}
        <div>
          <Icon value={detail.faqs} />
        </div>
        <div>
          <Icon value={detail.unbrand} />
        </div>
        <div>
          <Icon value={detail.local} />
        </div>
        <div>
          <Icon value={detail.api} />
        </div>
        {/* <div className={classes.tableSubTitle}></div>
        <div>
          <Icon value={detail.messenger} />
        </div>
        <div>
          <Icon value={detail.facebook} />
        </div>
        <div>
          <Icon value={detail.whatsapp} />
        </div>
        <div>
          <Icon value={detail.telegram} />
        </div>
        <div>
          <Icon value={detail.line} />
        </div> */}
        <div className={classes.tableSubTitle}></div>
        <div>
          <Icon value={detail.brand} />
        </div>
        <div>
          <Icon value={detail.custom} />
        </div>
        <div>
          <Icon value={detail.customer} />
        </div>
        <div>
          <Icon value={detail.feature} />
        </div>
        <div>
          <Icon value={detail.team} />
        </div>
      </div>
    );
  };

  const getPriceFeatures = (order: number): PlanData[] => {
    return t(`pricing.plan_${order}.features`, { returnObjects: true }) || [];
  };

  const getFeaturesList = (order: number) => {
    return getPriceFeatures(order).map((item, index) => {
      return <FeatureItem key={index} title={item.title} />;
    });
  };

  const FeatureItem = ({ title }: { title: string }) => {
    return (
      <li className="flex items-center space-x-3">
        <IconCheck style={{ color: theme.colors.indigo[8], width: 18 }} />
        <span className="text-sm">{title}</span>
      </li>
    );
  };

  const PriceCard = ({ data }: { data: PlanInfo }) => {
    const detail = t(`pricing.plan_${data.order}`, { returnObjects: true }) as PlanData;
    const noPay = [0, 4].includes(data.order);
    const hasPriceId = Boolean(data.api_id_monthly || data.api_id_yearly);

    return (
      <div className="flex w-[350px] mb-6 flex-col justify-between p-6 max-w-lg text-gray-900 bg-white rounded-lg border border-gray-100 shadow dark:border-gray-600 xl:p-8 dark:bg-gray-800 dark:text-white">
        <div>
          <div className="text-xl font-bold mb-2">{detail.title}</div>
          {hasPriceId && !noPay && (
            <div className="flex justify-center items-baseline mb-3">
              <Text fz={30}>${getPrice(detail.price)}</Text>
              <Text fz={10} c="#999">
                /{t(paymentCycle === 'monthly' ? 'pricing.month' : 'pricing.year')}
              </Text>
            </div>
          )}
          <Divider my="sm" />
          <ul className="pb-5">{getFeaturesList(data.order)}</ul>
        </div>
        {noPay || !hasPriceId ? (
          <Button
            radius="xl"
            size="md"
            component="a"
            href={data.order === 0 ? import.meta.env.VITE_ADMIN_URL : 'https://www.sparticle.com/ja/contact-us-jp'}
            target="_blank"
          >
            {data.order === 0 ? t('register') : t('pricing.contact_us')}
          </Button>
        ) : (
          <Button
            radius="xl"
            size="md"
            loading={checkoutLoading === data.order}
            onClick={() => {
              goCheckout(data);
            }}
          >
            {t('pricing.purchase')}
          </Button>
        )}
      </div>
    );
  };

  const isColShow = (order: number) => {
    return !isSm || (isSm && +planType === order);
  };

  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <Container size="xl" className="py-5 text-center">
      <AuthChoiceModal opened={authChoiceOpen} onClose={() => setAuthChoiceOpen(false)} />

      {/* v0.5: 订单确认 Modal，所有用户点订阅都先弹此 Modal */}
      <OrderConfirmModal
        opened={!!confirmPriceId}
        priceId={confirmPriceId || ''}
        initialPromo={urlPromo}
        successUrl={`${origin}/pricing?payment=success`}
        cancelUrl={`${origin}/pricing?payment=cancel`}
        onClose={() => {
          setConfirmPriceId(null);
          setCheckoutLoading(null);
        }}
      />

      <Text fz={40} fw={700} className="py-7">
        {t('pricing.main_title')}
      </Text>
      <SegmentedControl
        value={paymentCycle}
        onChange={(value) => setPaymentCycle(value)}
        data={[
          { label: t('pricing.monthly'), value: 'monthly' },
          { label: t('pricing.yearly'), value: 'yearly' },
        ]}
      />

      <Space h="lg" />

      <div className="flex justify-center gap-2 flex-wrap items-stretch px-10 md:justify-between">
        {planList.map((plan) => {
          return <PriceCard data={plan} key={plan.order} />;
        })}
      </div>

      <Space h="lg" />

      <Text fz={30} className="py-7">
        {t('pricing.compare')}
      </Text>

      <Space h="lg" />

      {isSm && (
        <div>
          <SegmentedControl
            value={planType}
            onChange={(value) => setPlanType(value)}
            data={[
              { label: t('pricing.plan_0.title'), value: '0' },
              { label: t('pricing.plan_1.title'), value: '1' },
              { label: t('pricing.plan_2.title'), value: '2' },
              { label: t('pricing.plan_3.title'), value: '3' },
              { label: t('pricing.plan_4.title'), value: '4' },
            ]}
          />
          <Space h="md" />
        </div>
      )}

      <Grid
        justify="center"
        grow={true}
        gutter={0}
        className="rounded-lg border border-gray-100 shadow dark:border-gray-600 pt-5"
        style={{
          marginLeft: '2rem',
          marginRight: '2rem',
        }}
      >
        <Grid.Col span={2}>
          <PlanTitle />
        </Grid.Col>
        {isColShow(0) && (
          <Grid.Col span={2}>
            <Plan data={{ order: 0 }} />
          </Grid.Col>
        )}
        {planList.map((plan) => {
          return (
            isColShow(plan.order) && (
              <Grid.Col span={2} key={plan.order}>
                <Plan data={plan} />
              </Grid.Col>
            )
          );
        })}
        {isColShow(4) && (
          <Grid.Col span={2}>
            <Plan data={{ order: 4 }} />
          </Grid.Col>
        )}
      </Grid>
    </Container>
  );
};

export default Pricing;
